import React, { useState } from "react";
import { useSelect, UseSelectStateChange } from "downshift";
import ClickAwayListener from "react-click-away-listener";
import classNames from "classnames";

import { PartialsBackgroundVariants } from "./PartialsBackgroundVariants";
import { StreamLineIcon } from "../../SvgIcon/StreamLineIcon";

interface SelectProps<T> {
  items: T[];
  onSelectionChange: (item: T) => void;
  renderItemInList: (item: T) => string | React.ReactChild;
  itemToString: (item: T) => string;
  label: string | React.ReactChild;
  placeholder?: string;
  backgroundVariant?: PartialsBackgroundVariants;
  className?: string;
  prefixIcon?: React.ReactChild;
  disabled?: boolean;
}

export const Select = <T extends {}>(props: SelectProps<T>) => {
  const {
    items,
    label,
    className,
    placeholder,
    prefixIcon,
    onSelectionChange,
    renderItemInList,
    itemToString,
    backgroundVariant = "light",
    disabled = false,
  } = props;

  const [isInputFocused, setIsInputFocused] = useState(false);

  const insuranceSelectOptions = {
    items: items,
    onSelectedItemChange: (stateChange: UseSelectStateChange<T>) => {
      const { selectedItem } = stateChange;

      selectedItem && onSelectionChange && onSelectionChange(selectedItem);
    },
  };

  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect(insuranceSelectOptions);

  const renderItems = (item: T, index: number) => (
    <li
      key={`${item}${index}`}
      className={classNames("insuranceSelect__option", {
        "insuranceSelect__option--highlighted": highlightedIndex === index,
      })}
      {...getItemProps({ item, index })}
    >
      {renderItemInList(item)}
    </li>
  );

  const isSelectFocused =
    isInputFocused || isOpen || selectedItem || placeholder;

  return (
    <ClickAwayListener onClickAway={() => setIsInputFocused(false)}>
      <div
        className={classNames(
          "insuranceSelect",
          disabled && "insuranceSelect--disabled",
          className
        )}
      >
        <label
          className={classNames("fs-5 insuranceSelect__label", {
            "insuranceSelect__label--focus": isSelectFocused,
            "insuranceSelect__label--prefixIcon": prefixIcon,
          })}
          {...getLabelProps()}
        >
          {label}
        </label>
        <button
          {...getToggleButtonProps({ disabled })}
          className={classNames("insuranceSelect__inner", {
            "insuranceSelect__inner--light": backgroundVariant === "light",
            "insuranceSelect__inner--blueLight":
              backgroundVariant === "blue-light",
          })}
          type="button"
          aria-label="toggle menu"
        >
          {prefixIcon}
          <span className="insuranceSelect__selectedItem">
            {(selectedItem && itemToString(selectedItem)) ?? placeholder}
          </span>
          {isOpen ? (
            <StreamLineIcon iconName="s-arrow-up" />
          ) : (
            <StreamLineIcon iconName="s-arrow-down" />
          )}
        </button>
        <ul className="insuranceSelect__optionsList" {...getMenuProps()}>
          {isOpen && items.map(renderItems)}
        </ul>
      </div>
    </ClickAwayListener>
  );
};
